/**
 * Any OHS First Aid Box API calls need to be on this page.
 * Use CacheKeys QUERY_KEYS.firstAidBox.root to invalidate.
 * Nothing here is persisted - box state changes with every submitted check.
 */
import {
    GetFirstAidAnalytics,
    GetFirstAidBox,
    GetFirstAidBoxListFilters,
    GetFirstAidBoxUsageForm,
    GetFirstAidCatalog,
    GetFirstAidCheckHistory,
    GetFirstAidDiscrepancies,
    GetFirstAidManagerOverview,
    GetFirstAidReportFilters,
    GetFirstAidRestockList,
    GetFirstAidUsageReport,
    GetFirstAiderCandidates,
    GetMyOhsAccess,
    GetOhsBuildingCheckFilters,
    GetOhsBuildingCheckForm,
    GetOhsBuildingCheckHistory,
    GetOhsBuildingCheckList,
    GetOhsBuildingCheckQuestions,
    GetOhsPeople,
    GetOhsPeopleCandidates
} from "../../services/FirstAidBoxService";
import { appQueryClient } from "../appQueryClient";
import { QUERY_CACHE_PROFILES, QUERY_KEYS } from "../queryConsts";
import { useBaseReactQuery } from "./useBaseReactQuery";
import { usePermissionGuard } from "../../hooks/usePermissionGuard";

const EMPTY_LIST = Object.freeze([]);
const EMPTY_PAGE = Object.freeze({ data: [], total: 0 });
const NO_ACCESS = Object.freeze({ canView: false, canViewBoxes: false, canManage: false, canManagePeople: false });
const FULL_ACCESS = Object.freeze({ canView: true, canViewBoxes: true, canManage: true, canManagePeople: true });
const FULL_ACCESS_SCOPES = ["caohsmanageedit"];

/**
 * Invalidates all First Aid Box queries, inactive ones included, so the next screen opened
 * after a check/edit never shows stale box state.
 */
export function invalidateFirstAidBoxQueries() {
    return appQueryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.firstAidBox.root],
        refetchType: "all"
    });
}

/**
 * First Aid Box - Table filters, box form dropdowns, and whether the user has boxes of their own.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useFirstAidBoxListFilters() {
    return useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.boxListFilters],
        async () => {
            const { data } = await GetFirstAidBoxListFilters();
            return data?.data ?? {};
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT
        }
    );
}

/**
 * First Aid Box - One box with its latest first aider and manager checks.
 * @param {string | number} boxId Box, or falsy while creating a new one.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useFirstAidBox(boxId) {
    return useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.box, boxId],
        async () => {
            const { data } = await GetFirstAidBox(boxId);
            return data?.data ?? null;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DETAILS,
            persist: false,
            refetchOnMount: true,
            enabled: Boolean(boxId)
        }
    );
}

/**
 * First Aid Box - The "log items used" screen behind a box's QR code: the box and each item's
 * current quantity. Open to any logged-in AMI user.
 * @param {string} boxCode Box code from the QR link.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useFirstAidBoxUsageForm(boxCode) {
    return useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.usageForm, boxCode],
        async () => {
            const { data } = await GetFirstAidBoxUsageForm(boxCode);
            return data?.data ?? null;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            refetchOnMount: true,
            enabled: Boolean(boxCode)
        }
    );
}

/**
 * First Aid Box - The item catalog every box is checked against (deleted items left out).
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useFirstAidCatalog() {
    const query = useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.catalog],
        async () => {
            const { data } = await GetFirstAidCatalog();
            return data?.data ?? EMPTY_LIST;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            refetchOnMount: true
        }
    );

    return { ...query, data: query.data ?? EMPTY_LIST };
}

/**
 * First Aid Box - Users a box can be assigned to.
 * @param {boolean} enabled Only fetch for users with the Management edit permission.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useFirstAiderCandidates(enabled) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.firstAiderCandidates],
        async () => {
            const { data } = await GetFirstAiderCandidates();
            return data?.data ?? EMPTY_LIST;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            enabled: Boolean(enabled)
        }
    );

    return { ...query, data: query.data ?? EMPTY_LIST };
}

/**
 * First Aid Box - Manager overview tiles and overdue/non-compliant lists.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useFirstAidManagerOverview() {
    return useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.managerOverview],
        async () => {
            const { data } = await GetFirstAidManagerOverview();
            return data?.data ?? null;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            refetchOnMount: true
        }
    );
}

/**
 * First Aid Box - Restock list, most-needed item first.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useFirstAidRestockList() {
    const query = useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.restockList],
        async () => {
            const { data } = await GetFirstAidRestockList();
            return data?.data ?? EMPTY_LIST;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            refetchOnMount: true
        }
    );

    return { ...query, data: query.data ?? EMPTY_LIST };
}

/**
 * First Aid Box - Items where a manager spot-check disagrees with the first aider.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useFirstAidDiscrepancies() {
    const query = useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.discrepancies],
        async () => {
            const { data } = await GetFirstAidDiscrepancies();
            return data?.data ?? EMPTY_LIST;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            refetchOnMount: true
        }
    );

    return { ...query, data: query.data ?? EMPTY_LIST };
}

/**
 * First Aid Box - Compliance by campus, most needed items and items expiring soon.
 * @param {number} expiryWindowDays How many days ahead counts as "expiring soon".
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useFirstAidAnalytics(expiryWindowDays) {
    return useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.analytics, expiryWindowDays],
        async () => {
            const { data } = await GetFirstAidAnalytics(expiryWindowDays);
            return data?.data ?? null;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            refetchOnMount: true,
            // Keeps the charts on screen while a new expiry window loads.
            keepPreviousData: true
        }
    );
}

/**
 * First Aid Box - Campus, box and check type options for the Usage and Check History reports.
 * @returns {DefinedUseQueryResult} React Query result - data is {} until loaded.
 */
export function useFirstAidReportFilters() {
    const query = useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.reportFilters],
        async () => {
            const { data } = await GetFirstAidReportFilters();
            return data?.data ?? {};
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT
        }
    );

    return { ...query, data: query.data ?? {} };
}

/**
 * First Aid Box - One page of the Usage report (items taken via QR code).
 * @param {Object} reportParams { page, limit, key, sort, search, viaCampus, viaBox, startDate, endDate }
 * @returns {DefinedUseQueryResult} React Query result - data is { data, total }.
 */
export function useFirstAidUsageReport(reportParams) {
    return useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.usageReport, reportParams],
        async () => {
            const { data } = await GetFirstAidUsageReport({ ...reportParams, exportStatus: "false" });
            return data?.data ?? EMPTY_PAGE;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.TABLE,
            refetchOnMount: true,
            keepPreviousData: true
        }
    );
}

/**
 * First Aid Box - One page of the Check History report (every check with its stock snapshot).
 * @param {Object} reportParams { page, limit, key, sort, search, viaCampus, viaBox, viaCheckType, startDate, endDate }
 * @returns {DefinedUseQueryResult} React Query result - data is { data, total }.
 */
export function useFirstAidCheckHistory(reportParams) {
    return useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.checkHistory, reportParams],
        async () => {
            const { data } = await GetFirstAidCheckHistory({ ...reportParams, exportStatus: "false" });
            return data?.data ?? EMPTY_PAGE;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.TABLE,
            refetchOnMount: true,
            keepPreviousData: true
        }
    );
}

/**
 * OHS - Every building with OHS Representatives assigned, with its latest check.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useOhsBuildingCheckList() {
    const query = useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.buildingCheckList],
        async () => {
            const { data } = await GetOhsBuildingCheckList();
            return data?.data ?? EMPTY_LIST;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            refetchOnMount: true
        }
    );

    return { ...query, data: query.data ?? EMPTY_LIST };
}

/**
 * OHS - Filter options for the Building Checks list and history, and the buildings and OHS
 * Representatives the assign popup offers.
 * @returns {DefinedUseQueryResult} React Query result - data is {} until loaded.
 */
export function useOhsBuildingCheckFilters() {
    const query = useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.buildingCheckFilters],
        async () => {
            const { data } = await GetOhsBuildingCheckFilters();
            return data?.data ?? {};
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT
        }
    );

    return { ...query, data: query.data ?? {} };
}

/**
 * OHS - A building and the checklist to answer for its monthly check.
 * @param {string | number} buildingId Building being checked.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useOhsBuildingCheckForm(buildingId) {
    return useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.buildingCheckForm, buildingId],
        async () => {
            const { data } = await GetOhsBuildingCheckForm(buildingId);
            return data?.data ?? null;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DETAILS,
            persist: false,
            refetchOnMount: true,
            enabled: Boolean(buildingId)
        }
    );
}

/**
 * OHS - The building checklist (deleted questions left out).
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useOhsBuildingCheckQuestions() {
    const query = useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.buildingCheckQuestions],
        async () => {
            const { data } = await GetOhsBuildingCheckQuestions();
            return data?.data ?? EMPTY_LIST;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            refetchOnMount: true
        }
    );

    return { ...query, data: query.data ?? EMPTY_LIST };
}

/**
 * OHS - One page of the Building Check History report (every building check with its answers).
 * @param {Object} reportParams { page, limit, key, sort, search, viaCampus, viaBuilding, viaCheckedBy, viaResult, startDate, endDate }
 * @returns {DefinedUseQueryResult} React Query result - data is { data, total }.
 */
export function useOhsBuildingCheckHistory(reportParams) {
    return useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.buildingCheckHistory, reportParams],
        async () => {
            const { data } = await GetOhsBuildingCheckHistory({ ...reportParams, exportStatus: "false" });
            return data?.data ?? EMPTY_PAGE;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.TABLE,
            refetchOnMount: true,
            keepPreviousData: true
        }
    );
}

/**
 * OHS - What the signed-in user can see. The OHS Management permission (caohsmanageedit) is full
 * access straight away; without it, access comes from the First Aiders / Managers lists (fetched).
 * @returns {Object} React Query result - data is NO_ACCESS until loaded; isPermissionLoading is
 *   true until role permissions have loaded.
 */
export function useOhsAccess() {
    const hasFullAccessPermission = usePermissionGuard({ scopes: FULL_ACCESS_SCOPES });
    const query = useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.myAccess],
        async () => {
            const { data } = await GetMyOhsAccess();
            return data?.data ?? NO_ACCESS;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT
        }
    );

    return {
        ...query,
        data: hasFullAccessPermission ? FULL_ACCESS : (query.data ?? NO_ACCESS),
        isLoading: hasFullAccessPermission ? false : query.isLoading,
        isPermissionLoading: hasFullAccessPermission === null,
    };
}

/**
 * OHS - The First Aiders or Managers list.
 * @param {string} role first_aider | ohs_representative
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useOhsPeople(role) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.people, role],
        async () => {
            const { data } = await GetOhsPeople(role);
            return data?.data ?? EMPTY_LIST;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            refetchOnMount: true
        }
    );

    return { ...query, data: query.data ?? EMPTY_LIST };
}

/**
 * OHS - Active users not yet on the given list, for the "add" dropdown.
 * @param {string} role first_aider | ohs_representative
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useOhsPeopleCandidates(role) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.firstAidBox.root, QUERY_KEYS.firstAidBox.peopleCandidates, role],
        async () => {
            const { data } = await GetOhsPeopleCandidates(role);
            return data?.data ?? EMPTY_LIST;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            refetchOnMount: true
        }
    );

    return { ...query, data: query.data ?? EMPTY_LIST };
}
